import type { InvocationContext } from 'hypertube-nodejs-sdk'

import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import type { ClassData, FieldData, LibraryData } from '@/core/types/library.type'

import { resolveClassDataForType } from './resolveClassDataForType'
import { RuntimeInstance } from './RuntimeInstance'

const MAX_UNPACK_DEPTH = 2

export type UnpackContext = {
  libraries: Record<string, LibraryData>
  fallbackClassData?: ClassData
  hintTypeName?: string
}

function isInvocationContext(value: unknown): value is InvocationContext {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as {
    getValue?: unknown
    getInstanceField?: unknown
    getResultType?: unknown
    retrieveArray?: unknown
  }
  return (
    typeof candidate.getValue === 'function' &&
    typeof candidate.getInstanceField === 'function' &&
    typeof candidate.getResultType === 'function' &&
    typeof candidate.retrieveArray === 'function'
  )
}

function getInstanceFields(classData: ClassData): FieldData[] {
  if (!classData.fields) return []
  return Object.values(classData.fields).filter((field) => field.type === UnifiedGraftModelTypeKey.INSTANCE_FIELD)
}

async function safeGetType(execution: InvocationContext): Promise<string | null> {
  try {
    return await execution.getResultType()
  } catch {
    return null
  }
}

export async function unpackInvocationResult(rawValue: unknown, context: UnpackContext, depth = 0): Promise<unknown> {
  if (rawValue === null || rawValue === undefined) return rawValue
  if (!isInvocationContext(rawValue)) return rawValue

  const execution = rawValue

  let primitive: unknown
  try {
    primitive = execution.getValue?.()
  } catch {
    primitive = undefined
  }

  const isScalarPrimitive =
    primitive !== undefined &&
    !isInvocationContext(primitive) &&
    (primitive === null || (typeof primitive !== 'object' && typeof primitive !== 'function'))

  if (isScalarPrimitive) return primitive as null | string | number | boolean | bigint | symbol

  try {
    const arr = await execution.retrieveArray()
    if (Array.isArray(arr)) {
      return Promise.all(arr.map((item) => unpackInvocationResult(item, context, depth + 1)))
    }
  } catch {
    /* fall through */
  }

  const typeName = await safeGetType(execution)
  const classData = resolveClassDataForType({
    runtimeTypeName: typeName,
    hintTypeName: depth === 0 ? context.hintTypeName : undefined,
    libraries: context.libraries,
    fallbackClassData: context.fallbackClassData,
  })

  const displayTypeName = classData?.name ?? typeName ?? context.hintTypeName ?? 'Object'

  if (depth >= MAX_UNPACK_DEPTH || !classData) {
    return new RuntimeInstance(displayTypeName, {})
  }

  const fields = getInstanceFields(classData)

  const entries = await Promise.all(
    fields.map(async (field): Promise<readonly [string, unknown] | null> => {
      try {
        const fieldExecution = await execution.getInstanceField(field.name).execute()
        const value = await unpackInvocationResult(fieldExecution, { libraries: context.libraries }, depth + 1)
        return [field.name, value] as const
      } catch {
        return null
      }
    })
  )

  const populated: Record<string, unknown> = {}
  for (const entry of entries) {
    if (entry) populated[entry[0]] = entry[1]
  }

  return new RuntimeInstance(displayTypeName, populated)
}
